import React, {
  useEffect, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { message, Select } from 'antd';
import { CaretDownOutlined, PlusOutlined } from '@ant-design/icons';

import {
  getReferredToProviderOptions,
  getReferralsQuery,
  setReferredToProvider,
} from '../../../../actions/records';
import { captureAnalytics } from '../../../../actions/preload';

import './referredToProviderCell.scss';

const REFERRER_INFO_TITLE = 'Referrer Info';
const NO_PROVIDER_VALUE = '__no_referred_to_provider__';

const assignmentSource = (provider) => (
  provider?.source === 'ai' || provider?.source === 'manual' ? provider.source : 'unknown'
);

export const normalizeProviderOptions = (response, currentProvider) => {
  const providers = Array.isArray(response?.data?.providers) ? response.data.providers : [];
  const uniqueProviders = new Map();

  providers.forEach((provider) => {
    if (provider?.id == null || !provider?.name) return;
    uniqueProviders.set(String(provider.id), provider);
  });

  if (currentProvider?.id != null && currentProvider?.name) {
    const currentId = String(currentProvider.id);
    if (!uniqueProviders.has(currentId)) uniqueProviders.set(currentId, currentProvider);
  }

  return Array.from(uniqueProviders.values());
};

export const ReferredToProviderCell = ({
  record,
  loadProviderOptions,
  saveProvider,
  refetchReferrals,
}) => {
  const referredToProvider = record?.referred_to_provider || null;
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [providers, setProviders] = useState(
    () => normalizeProviderOptions(undefined, referredToProvider),
  );
  const canEdit = record?.can_edit_referred_to_provider === true
    && Boolean(record?.send_token && record?.receiver_type);
  const mountedRef = useRef(true);
  const optionsLoadedRef = useRef(false);

  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const resetBusyFlagIfMounted = (setBusy) => {
    if (mountedRef.current) setBusy(false);
  };

  const openPicker = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (!canEdit) return;

    captureAnalytics('referred_to_provider_picker_opened', {
      has_assignment: Boolean(referredToProvider?.name),
      assignment_source: assignmentSource(referredToProvider),
    });
    setEditing(true);

    if (optionsLoadedRef.current || loading) return;
    setLoading(true);
    let response;
    try {
      response = await loadProviderOptions(record.send_token, record.receiver_type);
    } catch {
      response = { error: true };
    } finally {
      resetBusyFlagIfMounted(setLoading);
    }
    if (!mountedRef.current) return;

    if (response?.error) {
      captureAnalytics('referred_to_provider_options_load_failed');
      message.error(response?.response?.data?.error || 'Unable to load providers. Please try again.');
      setEditing(false);
      return;
    }

    setProviders(normalizeProviderOptions(response, referredToProvider));
    optionsLoadedRef.current = true;
  };

  const handleChange = async (value) => {
    const nextProviderId = value === NO_PROVIDER_VALUE ? null : value;
    const currentProviderId = referredToProvider?.id == null
      ? null
      : String(referredToProvider.id);
    if (nextProviderId === currentProviderId) {
      setEditing(false);
      return;
    }

    setSaving(true);
    let response;
    try {
      response = await saveProvider(
        record.send_token,
        nextProviderId,
        record.receiver_type,
      );
    } catch {
      response = { error: true };
    } finally {
      resetBusyFlagIfMounted(setSaving);
    }
    if (!mountedRef.current) return;

    if (response?.error) {
      captureAnalytics('referred_to_provider_update_failed', {
        cleared: nextProviderId === null,
        previous_assignment_source: assignmentSource(referredToProvider),
      });
      message.error(response?.response?.data?.error || 'Unable to update referred-to provider. Please try again.');
      return;
    }

    captureAnalytics('referred_to_provider_updated', {
      cleared: nextProviderId === null,
      previous_assignment_source: assignmentSource(referredToProvider),
    });
    message.success(nextProviderId === null
      ? 'Referred-to provider cleared.'
      : 'Referred-to provider updated.');
    setEditing(false);
    const currentQuery = typeof window === 'undefined' ? '' : window.location.search;
    refetchReferrals(currentQuery || '');
  };

  const displayedProviders = normalizeProviderOptions(
    { data: { providers } },
    referredToProvider,
  );
  const selectOptions = [
    ...(referredToProvider?.name ? [{
      value: NO_PROVIDER_VALUE,
      name: 'No provider',
      label: <span className="referred-to-provider-cell__clear">No provider</span>,
    }] : []),
    ...displayedProviders.map((provider) => ({
      value: String(provider.id),
      name: provider.name,
      label: provider.name,
    })),
  ];

  if (!canEdit) {
    return (
      <div className="referred-to-provider-cell">
        {referredToProvider?.name ? (
          <span className="referred-to-provider-cell__name">{referredToProvider.name}</span>
        ) : null}
      </div>
    );
  }

  return (
    <div className="referred-to-provider-cell">
      {editing ? (
        <Select
          className="referred-to-provider-cell__select"
          size="small"
          autoFocus
          defaultOpen
          showSearch
          aria-label="Select referred-to provider"
          loading={loading || saving}
          disabled={saving}
          optionLabelProp="label"
          placeholder="Select provider"
          value={referredToProvider?.id == null ? undefined : String(referredToProvider.id)}
          options={selectOptions}
          onChange={handleChange}
          onBlur={() => {
            if (!saving) setEditing(false);
          }}
          onClick={(event) => event.stopPropagation()}
          filterOption={(input, option) => (
            String(option?.name || '').toLowerCase().includes(input.toLowerCase())
          )}
          getPopupContainer={(trigger) => trigger.parentNode}
          style={{ width: '100%' }}
        />
      ) : (
        <button
          type="button"
          className="referred-to-provider-cell__button"
          disabled={!canEdit}
          aria-label={referredToProvider?.name
            ? `Change referred-to provider (currently ${referredToProvider.name})`
            : 'Set referred-to provider'}
          onClick={openPicker}
        >
          {referredToProvider?.name ? (
            <>
              <span className="referred-to-provider-cell__name">{referredToProvider.name}</span>
              <CaretDownOutlined className="referred-to-provider-cell__chevron" />
            </>
          ) : (
            <span className="referred-to-provider-cell__add">
              <PlusOutlined className="referred-to-provider-cell__icon" />
              {' Set provider'}
            </span>
          )}
        </button>
      )}
    </div>
  );
};

ReferredToProviderCell.propTypes = {
  record: PropTypes.object.isRequired,
  loadProviderOptions: PropTypes.func,
  saveProvider: PropTypes.func,
  refetchReferrals: PropTypes.func,
};

ReferredToProviderCell.defaultProps = {
  loadProviderOptions: async () => ({ data: { providers: [] } }),
  saveProvider: async () => ({}),
  refetchReferrals: () => {},
};

const ConnectedReferredToProviderCell = connect(null, {
  loadProviderOptions: getReferredToProviderOptions,
  saveProvider: setReferredToProvider,
  refetchReferrals: getReferralsQuery,
})(ReferredToProviderCell);

export const addReferredToProviderColumn = (columns, enabled) => {
  if (!enabled) return columns;

  const referrerInfoIndex = columns.findIndex((column) => column?.title === REFERRER_INFO_TITLE);
  if (referrerInfoIndex === -1) return columns;

  const nextColumns = [...columns];
  nextColumns.splice(referrerInfoIndex + 1, 0, {
    title: 'Referred To',
    dataIndex: 'referred_to_provider',
    className: 'referredToProvider',
    width: 160,
    render: (_text, record) => <ConnectedReferredToProviderCell record={record} />,
  });
  return nextColumns;
};

export default ConnectedReferredToProviderCell;
